import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CursorPaginatedResponse,
  PackageDeal,
  PackageDealData,
  PackageDealListParams,
} from '@/libs/interfaces'
import { IS_MOCK_MODE } from '@/libs/mocks/config'
import { packageDealsApiMock } from '@/libs/mocks/handlers/packageDeals'

const packageDealsApiReal = {
  listCursor: async (
    params: PackageDealListParams,
  ): Promise<CursorPaginatedResponse<PackageDeal>> => {
    const { orgID, storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<PackageDealData>>(URLs.PackageDeals.list(orgID, storeID, query))
      .then((res) => res?.data?.data)
    return {
      data: response.packageDeals ?? [],
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  list: async (params: PackageDealListParams): Promise<PackageDeal[]> => {
    const { orgID, storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<PackageDealData>>(URLs.PackageDeals.list(orgID, storeID, query))
      .then((res) => res?.data?.data?.packageDeals ?? [])
  },

  getByID: async (params: {
    orgID: string
    storeID: string
    packageID: string
  }): Promise<Partial<PackageDeal>> => {
    const { orgID, storeID, packageID } = params
    return await api
      .get<ApiResponse<Partial<PackageDeal>>>(URLs.PackageDeals.byID(orgID, storeID, packageID))
      .then((res) => res.data.data)
  },

  create: async (body: Partial<PackageDeal>): Promise<Partial<PackageDeal>> => {
    const { orgID = '', storeID = '', ...payload } = body
    return await api
      .post<ApiResponse<Partial<PackageDeal>>>(URLs.PackageDeals.create(orgID, storeID), payload)
      .then((res) => res.data.data)
  },

  update: async (payload: Partial<PackageDeal>): Promise<Partial<PackageDeal>> => {
    const { orgID = '', storeID = '', packageID = '', ...patchData } = payload
    return await api
      .patch<ApiResponse<Partial<PackageDeal>>>(
        URLs.PackageDeals.byID(orgID, storeID, packageID),
        patchData,
      )
      .then((res) => res.data.data)
  },

  delete: async (payload: Partial<PackageDeal>): Promise<Partial<PackageDeal>> => {
    const { orgID = '', storeID = '', packageID = '' } = payload
    return await api
      .delete<ApiResponse<Partial<PackageDeal>>>(URLs.PackageDeals.byID(orgID, storeID, packageID))
      .then((res) => res.data?.data)
  },
}

export const packageDealsApi: typeof packageDealsApiReal = IS_MOCK_MODE
  ? packageDealsApiMock
  : packageDealsApiReal
